import { useState } from "react";
import Layout, { Main } from "../Layout";
import {
  Alert,
  CardFooter,
  CardSubtitle,
  CardText,
  Col,
  Container,
  Row,
  Toast,
  ToastBody,
  ToastHeader,
  Button,
} from "reactstrap";
import Switch from "../Switch";
import styled from "styled-components";
import { colors, mediaQueries } from "../theme";
import { Link } from "react-router-dom";
import CurrencyConverter from "../utils/CurrencyConverter";
import GenericError from "../Error/GenericError";

const ImageWrapper = styled.div`
  width: 130px;
  height: 130px;
  img {
    width: 100%;
    height: 100%;
    border-radius: 100rem;
    border: 2px solid
      ${({ theme }) => (theme.mode === "dark" ? "white" : "black")};
  }
  ${mediaQueries.desktop} {
    width: 186px;
    height: 186px;
  }
`;

const Price = styled.span`
  margin-top: 5px;
  background-color: ${({ theme }) => theme.primaryOne};
  color: white;
  padding: 2px 10px;
  border-radius: 15px;
  font-weight: 600;
`;

const VideoWrapper = styled.div`
  position: relative;
  margin-bottom: 40px;
  padding: 0;
  width: 100%;
  video {
    width: 100%;
  }
  :before {
    content: "Commandez des contenus personnalisés auprès de vos célébrités.";
    display: flex;
    width: 100%;
    height: 99%;
    align-items: center;
    justify-content: center;
    padding: 0 15px;
    text-align: center;
    background-color: rgba(0, 0, 0, 0.7);
    position: absolute;
    top: 0;
    font-size: 15px;
    font-weight: bold;
    color: white;
    letter-spacing: 2px;
  }
  ${mediaQueries.desktop} {
    :before {
      font-size: 32px;
    }
  }
`;

const CelebrityListScrollWrapper = styled(Row)`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: scroll;
  scrollbar-width: none;
`;

const BgContainer = styled(Container)`
  background: linear-gradient(#5b5030fa, transparent);
`;

const HowItWorkScrollWrapper = styled.div`
  padding: 40px 24px;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: scroll;
  scrollbar-width: none;
`;

const ToastWrapper = styled(Toast)`
  margin: 20px 20px 0 0;
  width: 480px !important;
  min-width: 300px;
  ${mediaQueries.desktop} {
    margin: 0 40px 0 0;
    min-width: 500px;
  }
`;

const CelebrityCard = styled.div`
  width: 150px;
  height: 100%;
  ${mediaQueries.desktop} {
    width: 210px;
  }
`;

const CelebrityInfo = styled.div`
  padding-top: 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.text};
  ${mediaQueries.desktop} {
    font-size: 14px;
  }
`;
const Illustration = styled.img`
  width: 100%;
`;

export const getTheMostAttractivePrice = (celebrity) => {
  const prices = celebrity.products.map((p) => p.price);
  return Math.min(...prices);
};

const Home = ({ celebrities, celebritiesError }) => {
  const [isEuro, setIsEuro] = useState(true);

  if (celebritiesError) {
    return (
      <Layout>
        <Main>
          <Alert color={"danger"}>
            <h1 className={"fs-4"}>Une erreur est survenue.</h1>
            <p>
              Nos équipes sont mobilisées pour rétablir le service dans les
              meilleurs délai.
            </p>
          </Alert>
        </Main>
      </Layout>
    );
  }
  return (
    <Layout celebrities={celebrities.items}>
      <Main>
        {celebrities.items.length >= 0 && (
          <Container className={"px-0"} fluid>
            <Row>
              <Col>
                <Switch
                  state={isEuro}
                  setState={setIsEuro}
                  trueLabel={"EURO"}
                  falseLabel={"CFA"}
                />
              </Col>
            </Row>
            <h2 className={"mt-4"}>Toutes nos célébrités</h2>
          </Container>
        )}
        <Container className={"px-0"} fluid>
          {celebrities.items.length > 0 ? (
            <CelebrityListScrollWrapper>
              {celebrities.items.map((celebrity, index) => (
                <CelebrityCard key={index} xs={6} md={2} className={"my-5"}>
                  <Link
                    to={`/shop/${celebrity.id}/infos`}
                    state={{
                      celebrity: celebrity,
                      currency: isEuro ? "euro" : "cfa",
                    }}
                    className={"text-decoration-none"}
                  >
                    <ImageWrapper>
                      <img
                        src={celebrity.avatar}
                        title={`photo de ${celebrity.givenName} ${celebrity.familyName}`}
                        alt={`photo de ${celebrity.givenName} ${celebrity.familyName}`}
                        className={"object-fit-cover"}
                      />
                    </ImageWrapper>

                    <CelebrityInfo>
                      <CardText
                        tag={"div"}
                        className={"fw-bold text-center text-capitalize"}
                      >
                        {`${
                          celebrity.givenName
                        } ${celebrity.familyName.toUpperCase()}`}
                      </CardText>
                      <CardSubtitle
                        tag={"div"}
                        className={"text-center text-capitalize"}
                      >
                        {celebrity.title}
                      </CardSubtitle>
                      <CardText
                        className={
                          "d-flex justify-content-around align-items-baseline"
                        }
                      >
                        <Price>
                          {CurrencyConverter(
                            getTheMostAttractivePrice(celebrity),
                            celebrity.products.find(
                              (p) =>
                                p.price === getTheMostAttractivePrice(celebrity)
                            ).currency,
                            isEuro ? "euro" : "cfa"
                          )}
                        </Price>
                      </CardText>
                    </CelebrityInfo>
                  </Link>
                </CelebrityCard>
              ))}
            </CelebrityListScrollWrapper>
          ) : (
            <p className={"fs-4 py-4"}>
              Aucune données disponible sur les célébrités pour le moment.
            </p>
          )}
        </Container>
      </Main>
      <hr className={"w-50 mx-auto"} />
      <BgContainer className={"px-0 mb-0 mt-4"} fluid>
        <h2 className={"text-white pt-4 px-4"}>Comment ça marche ?</h2>

        <HowItWorkScrollWrapper>
          <ToastWrapper className={"bg-white text-white"}>
            <ToastHeader
              icon={<i className={"fa fa-search"} />}
              className={"bg-dark text-white border-secondary"}
            >
              TROUVEZ UNE CÉLÉBRITÉ
            </ToastHeader>
            <Row className={"align-items-center"}>
              <Col md={5} className={"d-flex justify-content-center"}>
                <Illustration
                  className={"w-75"}
                  src="choose-celebrity.jpeg"
                  alt=""
                />
              </Col>
              <Col md={7}>
                <ToastBody className={"bg-white text-black fs-6"}>
                  Trouvez votre célébrité préférée parmi les centaines de stars
                  qui offrent des dédicaces vidéos, des dédicaces musicales et
                  des vidéos business sur @llodedicace.
                </ToastBody>
              </Col>
            </Row>
          </ToastWrapper>
          <ToastWrapper className={"bg-white text-white"}>
            <ToastHeader
              icon={<i className={"fa fa-pencil"} />}
              className={"bg-primary text-white border-secondary-subtle"}
            >
              CHOISISSEZ VOTRE PRODUIT
            </ToastHeader>
            <Row className={"align-items-center"}>
              <Col md={5} className={"d-flex justify-content-center"}>
                <Illustration
                  className={"w-75"}
                  src="choose-your-product.jpeg"
                  alt=""
                />
              </Col>
              <Col md={7}>
                <ToastBody className={"bg-white text-black fs-6"}>
                  Vous pouvez choisir le type de contenu personalisé que vous
                  souhaitez commander etdonner plus de détails à votre célébrité
                  sur votre commande.
                </ToastBody>
              </Col>
            </Row>
          </ToastWrapper>
          <ToastWrapper className={"bg-white text-white"}>
            <ToastHeader
              icon={<i className={"fa fa-envelope"} />}
              className={"bg-secondary text-white border-secondary-subtle"}
            >
              COMPLETEZ VOTRE COMMANDE
            </ToastHeader>
            <Row className={"align-items-center"}>
              <Col md={5} className={"d-flex justify-content-center"}>
                <Illustration
                  className={"w-75"}
                  src="add-order-infos.jpeg"
                  alt=""
                />
              </Col>
              <Col md={7}>
                <ToastBody className={"bg-white text-black fs-6"}>
                  Complétez les informations sur votre commande afin de
                  permettre à votre célébrité de vous proposer un contenu
                  personalisé.
                </ToastBody>
              </Col>
            </Row>
          </ToastWrapper>
          <ToastWrapper className={"bg-white text-white"}>
            <ToastHeader
              icon={<i className={"fa fa-download"} />}
              className={"bg-success text-white border-secondary-subtle"}
            >
              PAYEZ VOTRE COMMANDE
            </ToastHeader>
            <Row className={"align-items-center"}>
              <Col md={5} className={"d-flex justify-content-center"}>
                <Illustration
                  className={"w-75"}
                  src="pay-your-order.jpeg"
                  alt=""
                />
              </Col>
              <Col md={7}>
                <ToastBody className={"bg-white text-black fs-6"}>
                  Payez en toute sécurité votre commande en utilisant les moyens
                  de paiement sûrs et sécurisés proposés par nos partenaires.
                </ToastBody>
              </Col>
            </Row>
          </ToastWrapper>
        </HowItWorkScrollWrapper>
      </BgContainer>
      <hr className={"w-50 mx-auto"} />

      <Main>
        <h2 className={"fw-bold mb-5"}>Nos produits</h2>
        <Row className={"justify-content-around"}>
          <Col md={4} className={"mb-md-0 mb-5"}>
            <img
              className={"w-100"}
              src="video_dedicace_illustration.jpeg"
              alt=""
            />
          </Col>
          <Col md={4} className={"mb-md-0 mb-5"}>
            <img
              className={"w-100"}
              src="music_dedicace_illustration.jpeg"
              alt=""
            />
          </Col>
          <Col md={4}>
            <img
              className={"w-100"}
              src="business_video_illustration.jpeg"
              alt=""
            />
          </Col>
        </Row>
        <Row className={"mt-5 mt-md-5 mb-5"}>
          <Col md={3} className={"mx-auto"}>
            <Button
              className={"w-100 p-3 fw-bold"}
              color="primary"
              to="/about-us"
              tag={Link}
            >
              En savoir plus sur nos produits
            </Button>
          </Col>
        </Row>
      </Main>
    </Layout>
  );
};

export default Home;
